import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import {
  CheckCircle2,
  Clock,
  Play,
  Pause,
  SkipForward,
  ChevronRight,
  Loader2,
  Calendar,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Milestone status configs
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  skipped: { label: 'Skipped', color: 'bg-yellow-100 text-yellow-700', icon: SkipForward },
};

/**
 * MilestoneTracker Component
 * Shows milestone progress for quote-based work
 * Used in both Provider Dashboard and Customer view
 */
const MilestoneTracker = ({ 
  quoteId, 
  providerId,
  isProvider = false, // If true, shows provider controls
  onMilestoneUpdate,
}) => {
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState([]);
  const [progress, setProgress] = useState({ total: 0, completed: 0, percentage: 0 });
  const [updating, setUpdating] = useState(null);
  const [notes, setNotes] = useState({});
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('custom_dress');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (quoteId) {
      fetchMilestones();
    }
    fetchTemplates();
  }, [quoteId]);

  const fetchMilestones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/services/quotes/${quoteId}/milestones`);
      setMilestones(response.data.milestones || []);
      setProgress(response.data.progress || { total: 0, completed: 0, percentage: 0 });
    } catch (error) {
      console.error('Failed to fetch milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services/milestones/templates`);
      setTemplates(Object.keys(response.data.templates || {}));
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const createMilestones = async () => {
    try {
      setCreating(true);
      await axios.post(
        `${API_URL}/api/services/quotes/${quoteId}/milestones?template_type=${selectedTemplate}`
      );
      await fetchMilestones();
    } catch (error) {
      console.error('Failed to create milestones:', error);
    } finally {
      setCreating(false);
    }
  };

  const updateMilestoneStatus = async (milestoneId, status) => {
    try {
      setUpdating(milestoneId);
      await axios.put(`${API_URL}/api/services/milestones/${milestoneId}`, {
        status: status,
        notes: notes[milestoneId] || null,
      });
      await fetchMilestones();
      onMilestoneUpdate?.();
    } catch (error) {
      console.error('Failed to update milestone:', error);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          <span className="ml-2">Loading milestones...</span>
        </CardContent>
      </Card>
    );
  }

  // No milestones yet - show creation UI for providers
  if (milestones.length === 0 && isProvider) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Set Up Milestones
          </CardTitle>
          <CardDescription>
            Create milestones to track progress on this custom work
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Select Template</label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={createMilestones} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Create Milestones
                <ChevronRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (milestones.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>No milestones set up yet</p>
          <p className="text-sm">The provider will add progress tracking soon</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="milestone-tracker">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Work Progress</CardTitle>
            <CardDescription>
              {progress.completed} of {progress.total} milestones completed
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-purple-600">{progress.percentage}%</p>
            <p className="text-xs text-gray-500">Complete</p>
          </div>
        </div>
        <Progress value={progress.percentage} className="h-2 mt-2" />
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {milestones.map((milestone, index) => {
            const status = STATUS_CONFIG[milestone.status] || STATUS_CONFIG.pending;
            const Icon = status.icon;
            const isActive = milestone.status === 'in_progress';
            const canStart = milestone.status === 'pending' && 
              (index === 0 || milestones[index - 1]?.status === 'completed');
            
            return (
              <div 
                key={milestone.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isActive 
                    ? 'border-blue-400 bg-blue-50' 
                    : milestone.status === 'completed'
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200'
                }`}
                data-testid={`milestone-${milestone.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {/* Step Number / Icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      milestone.status === 'completed' 
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {milestone.status === 'completed' ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <span className="font-semibold text-sm">{index + 1}</span>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{milestone.name}</h4>
                      {milestone.description && (
                        <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                      )}
                      
                      {/* Meta Info */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        {milestone.estimated_days && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            ~{milestone.estimated_days} days
                          </span>
                        )}
                        {milestone.payment_percentage > 0 && (
                          <span className="text-purple-600 font-medium">
                            {milestone.payment_percentage}% payment
                          </span>
                        )}
                      </div>
                      
                      {/* Completion Info */}
                      {milestone.completed_at && (
                        <p className="text-xs text-green-600 mt-2">
                          ✓ Completed on {new Date(milestone.completed_at).toLocaleDateString()}
                        </p>
                      )}
                      
                      {/* Provider Notes Input */}
                      {isProvider && isActive && (
                        <div className="mt-3">
                          <Textarea
                            placeholder="Add notes about this milestone..."
                            value={notes[milestone.id] || ''}
                            onChange={(e) => setNotes({ ...notes, [milestone.id]: e.target.value })}
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      )}
                      
                      {/* Milestone Notes */}
                      {milestone.notes && (
                        <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                          <p className="text-gray-600">{milestone.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Badge & Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={status.color}>
                      <Icon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                    
                    {/* Provider Actions */}
                    {isProvider && (
                      <div className="flex gap-1">
                        {canStart && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300"
                            onClick={() => updateMilestoneStatus(milestone.id, 'in_progress')}
                            disabled={updating === milestone.id}
                          >
                            {updating === milestone.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Play className="h-3 w-3 mr-1" />
                                Start
                              </>
                            )}
                          </Button>
                        )}
                        {isActive && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => updateMilestoneStatus(milestone.id, 'completed')}
                            disabled={updating === milestone.id}
                          >
                            {updating === milestone.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Complete
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default MilestoneTracker;
